import * as React from "react"
import { useTranslation } from "react-i18next"

import useCatalogNavigation from "Catalog/shared/hooks/useCatalogNavigation"
import { IOptionList } from "shared/hubrise/models"
import { Option, OptionList as OptionListUtil } from "shared/hubrise/utils"

import {
  Name,
  Options,
  OptionLabel,
  OptionInput,
  Price,
  Selection,
  StyledOptionList,
  Title,
  NameAndPrice,
} from "./Styles"

interface OptionListProps {
  optionList: IOptionList
  optionNames: Array<string>
  setOptionNames: (names: Array<string>) => void
  namePrefix?: string
}

const OptionList = ({ optionList, optionNames, setOptionNames, namePrefix }: OptionListProps) => {
  const { t } = useTranslation()
  const { variantRef } = useCatalogNavigation()

  const isValid =
    optionNames.length >= optionList.min_selections &&
    (optionList.max_selections === null || optionNames.length <= optionList.max_selections)

  const onOptionChange = (optionName: string) => {
    if (OptionListUtil.isSingleChoice(optionList)) {
      setOptionNames([optionName])
    } else {
      if (optionNames.includes(optionName)) {
        setOptionNames(optionNames.filter((name) => name !== optionName))
      } else {
        setOptionNames([...optionNames, optionName])
      }
    }
  }

  const selectionText: string | undefined = (() => {
    const { min_selections, max_selections } = optionList
    if (min_selections === max_selections) {
      return min_selections !== 1 ? t("preview.popup.option_list.select_n", { min: min_selections }) : undefined
    }
    if (max_selections === null) {
      return min_selections >= 1 ? t("preview.popup.option_list.select_at_least_n", { min: min_selections }) : undefined
    }
    if (min_selections === 0) {
      return t("preview.popup.option_list.select_up_to_n", { max: max_selections })
    }
    return t("preview.popup.option_list.select_n_to_m", { min: min_selections, max: max_selections })
  })()

  const options = optionList.options.filter((option) => Option.isVisibleInPreview(option, variantRef))

  return (
    <StyledOptionList isValid={isValid}>
      <Title>{optionList.name}</Title>

      {selectionText !== undefined && <Selection isValid={isValid}>{selectionText}</Selection>}

      <Options>
        {options.map((option) => {
          const isChecked = optionNames.includes(option.name)
          const price = Option.price(option, variantRef)

          return (
            <OptionLabel key={option.id}>
              <OptionInput
                type={OptionListUtil.isSingleChoice(optionList) ? "radio" : "checkbox"}
                name={[namePrefix, optionList.name].filter((x) => x !== undefined).join("-")}
                checked={isChecked}
                onChange={() => onOptionChange(option.name)}
              />

              <NameAndPrice isActive={isChecked}>
                <Name>{option.name}</Name>
                {price.cents > 0 && <Price dangerouslySetInnerHTML={{ __html: price.toHumanString() }} />}
              </NameAndPrice>
            </OptionLabel>
          )
        })}
      </Options>
    </StyledOptionList>
  )
}

export default OptionList
